import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AdvisorAccessFormik,
  initialValuesAdvisorAccess,
  validationSchemaAdvisorAccess,
} from '@/modules/entity/components/common/AdvisorAccess.tsx';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { CustomCheckbox, RadioButton } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { UpdateEntityRequest } from 'api';

const radioButtonItems = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

export const AdvisorAccessHolder = ({
  moveForward,
  entity_type_prefix,
  allowSkip,
}: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const formik = useFormik<AdvisorAccessFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaAdvisorAccess,
    initialValues: {
      ...initialValuesAdvisorAccess,
      ...(dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix] as AdvisorAccessFormik),
    },
    onSubmit: async values => {
      setDirty(false);
      if (formik.dirty) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            [entity_type_prefix]: {
              ...dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix],
              ...values,
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeCheckbox = (field: string, checked: boolean) => {
    formik.setFieldValue(field, checked);
  };

  const handleChangeRadio = (value: boolean) => {
    formik.setFieldValue('is_financial_adviser_operate', value);
    formik.setFieldError('is_financial_adviser_operate', '');
  };

  return (
    <OnboardingHolderWrapper
      title="Access to information"
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col space-y-8">
        <div className="w-full flex flex-col space-y-4">
          <p className="text-[14px] leading-[22px]">
            Unless you elect otherwise, your authorised representative, agent and/or financial
            adviser will also be provided access to your investment information and/or receive
            copies of statements and transaction confirmations. By appointing an authorised
            representative, agent and/or financial adviser you acknowledge that you have read and
            agreed to the terms and conditions in the IM relating to such appointment.
          </p>
          <CustomCheckbox
            label="Please tick this box if you DO NOT want your authorised representative, agent and/or financial adviser to have access to information about your investment. (optional)"
            checked={formik.values.is_not_authorized_representative}
            onChange={e =>
              handleChangeCheckbox('is_not_authorized_representative', e.target.checked)
            }
          />
          <CustomCheckbox
            label="Please tick this box if you DO NOT want copies of statements and transaction confirmations sent to your authorised representative, agent and/or financial adviser. (optional)"
            checked={formik.values.is_not_copies}
            onChange={e => handleChangeCheckbox('is_not_copies', e.target.checked)}
          />
          <CustomCheckbox
            label="Please tick this box if you want statements and transaction confirmations sent ONLY to your authorised representative, agent and/or financial adviser. (optional)"
            checked={formik.values.is_only_authorized_representative}
            onChange={e =>
              handleChangeCheckbox('is_only_authorized_representative', e.target.checked)
            }
          />
        </div>
        <div className="w-full flex flex-col">
          <p className="text-[14px] leading-[22px] pb-4">
            Do you have permission from this investor to operate their account?
          </p>
          <RadioButton
            items={radioButtonItems}
            value={formik.values.is_financial_adviser_operate}
            onChange={value => handleChangeRadio(value)}
          />
          {formik.errors.is_financial_adviser_operate && (
            <p className="mt-1 text-sm text-[12px] text-error-red">
              {formik.errors.is_financial_adviser_operate}
            </p>
          )}
        </div>
      </div>
    </OnboardingHolderWrapper>
  );
};
